import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchMvpPilotState,
  fetchMvpQuoteDetail,
  fetchMvpQuotes,
  fetchMvpWorkOrderDetail,
  fetchMvpWorkOrders,
  updateMvpQuoteLine,
  type MvpPilotState,
  type MvpQuoteDetailRecord,
  type MvpQuoteRecord,
  type MvpQuoteLineUpdateInput,
  type MvpWorkOrderDetailRecord,
  type MvpWorkOrderRecord,
} from './mvpApi.ts';

export const MVP_QUERY_KEY_ROOT = ['field-service-technique-mvp'];

async function swallowErrors<T>(promise: Promise<T>): Promise<T | null> {
  try {
    return await promise;
  } catch {
    return null;
  }
}

export function useMvpQuoteData(enabled: boolean) {
  const quotesQuery = useQuery({
    queryKey: [...MVP_QUERY_KEY_ROOT, 'quotes'],
    queryFn: ({ signal }) => swallowErrors(fetchMvpQuotes(signal)),
    enabled,
    staleTime: 30_000,
    retry: false,
  });

  const quoteId = quotesQuery.data?.[0]?.id;

  const quoteDetailQuery = useQuery({
    queryKey: [...MVP_QUERY_KEY_ROOT, 'quotes', quoteId],
    queryFn: ({ signal }) => swallowErrors(fetchMvpQuoteDetail(quoteId!, signal)),
    enabled: enabled && Boolean(quoteId),
    staleTime: 30_000,
    retry: false,
  });

  return {
    quotes: (quotesQuery.data as MvpQuoteRecord[] | null | undefined) ?? null,
    quoteDetail: (quoteDetailQuery.data as MvpQuoteDetailRecord | null | undefined) ?? null,
  };
}

export function useUpdateMvpQuoteLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      quoteId,
      lineId,
      payload,
    }: {
      quoteId: string;
      lineId: string;
      payload: MvpQuoteLineUpdateInput;
    }) => updateMvpQuoteLine(quoteId, lineId, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: MVP_QUERY_KEY_ROOT });
    },
  });
}

export function useMvpWorkOrderData(selectedWorkOrderId: string, enabled: boolean) {
  const workOrdersQuery = useQuery({
    queryKey: [...MVP_QUERY_KEY_ROOT, 'work-orders'],
    queryFn: ({ signal }) => swallowErrors(fetchMvpWorkOrders(signal)),
    enabled,
    staleTime: 30_000,
    retry: false,
  });

  const selectedWorkOrderQuery = useQuery({
    queryKey: [...MVP_QUERY_KEY_ROOT, 'work-orders', selectedWorkOrderId],
    queryFn: ({ signal }) => swallowErrors(fetchMvpWorkOrderDetail(selectedWorkOrderId, signal)),
    enabled: enabled && Boolean(selectedWorkOrderId),
    staleTime: 30_000,
    retry: false,
  });

  return {
    workOrders: (workOrdersQuery.data as MvpWorkOrderRecord[] | null | undefined) ?? null,
    selectedWorkOrder: (selectedWorkOrderQuery.data as MvpWorkOrderDetailRecord | null | undefined) ?? null,
  };
}

export function useMvpPilotData(enabled: boolean) {
  const pilotStateQuery = useQuery({
    queryKey: [...MVP_QUERY_KEY_ROOT, 'pilot-state'],
    queryFn: ({ signal }) => swallowErrors(fetchMvpPilotState(signal)),
    enabled,
    staleTime: 30_000,
    retry: false,
  });

  return {
    pilotState: (pilotStateQuery.data as MvpPilotState | null | undefined) ?? null,
  };
}
